import React from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  Avatar,
  Box,
  Stack,
  Typography,
  Rating,
  Chip,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import { Link } from 'react-router-dom';
import TextOnlyChip from './TextOnlyChip';
import IconTextChip from './IconTextChip';
import { baseChipSx, baseLabelSx } from './chipStyles';

const PreviewText = styled(Typography)({
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
});

export default function TutorCard({ tutor, selectedSubjectIds = [] }) {
  const theme = useTheme();
  const ratingValue = tutor?.average_rating ?? tutor?.rating;

  const lowestRate = (() => {
    const offerings = selectedSubjectIds.length
      ? tutor.course_offerings?.filter((co) =>
          selectedSubjectIds.includes(co.subject?.id),
        )
      : tutor.course_offerings;
    let min = Infinity;
    offerings?.forEach((co) => {
      co.rates?.forEach((r) => {
        const price = parseFloat(r.price);
        const max = r.max_students || 1;
        const perPerson = price / max;
        if (!isNaN(perPerson) && perPerson < min) min = perPerson;
      });
    });
    return min === Infinity ? null : min;
  })();

  return (
    <Card
      variant="outlined"
      sx={{ mb: 2, position: 'relative', borderRadius: 1 }}
    >
      <CardActionArea
        component={Link}
        to={`/tutors/${tutor.id}`}
      >
        <CardContent
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            p: 2,
          }}
        >
          <Avatar
            alt={tutor.user?.username}
            src={tutor.user?.avatar || ''}
            sx={{ width: 56, height: 56 }}
          />

          <Stack spacing={0.5} sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {tutor.full_name}
                </Typography>
                {tutor.is_verified && (
                  <Chip
                    label="Verified"
                    color="success"
                    variant="outlined"
                    sx={{
                      ...baseChipSx,
                      '& .MuiChip-label': { ...baseLabelSx },
                    }}
                  />
                )}
              </Box>
              {ratingValue !== undefined && ratingValue !== null && (
                <Rating
                  value={Number(ratingValue)}
                  precision={0.1}
                  readOnly
                  size="small"
                />
              )}
            </Box>

            <PreviewText variant="body2" color="text.secondary">
              {tutor.intro}
            </PreviewText>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 1,
                alignItems: 'flex-end',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  flexGrow: 1,
                }}
              >
                {tutor.subjects.map((s) => {
                  const selected = selectedSubjectIds.includes(s.id);
                  return (
                    <TextOnlyChip
                      key={s.id}
                      label={s.name}
                      color="secondary"
                      sx={{ px: 0.5 }}
                      selected={selected}
                    />
                  );
                })}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {lowestRate !== null && (
                  <IconTextChip
                    icon={<AttachMoneyIcon />}
                    label={`${lowestRate.toFixed(2)}+`}
                    sx={{
                      '& .MuiChip-icon': {
                        fontSize: '1.3rem',
                        mr: '-5px',
                        ml: '-1px',
                      },
                      borderColor: theme.palette.info.dark,
                      color: theme.palette.info.dark,
                    }}
                  />
                )}
                {tutor.total_hours_tutored !== undefined && (
                  <IconTextChip
                    icon={<HourglassBottomIcon />}
                    label={`Tutored ${tutor.total_hours_tutored} hour${
                      tutor.total_hours_tutored === 1 ? '' : 's'
                    }`}
                    sx={{
                      borderColor: theme.palette.info.dark,
                      color: theme.palette.info.dark,
                    }}
                  />
                )}
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

